import React, { useState, useEffect, useCallback } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from '../ToastMessages/ToastMessageProvider';
import { getUserEntity } from 'common/UserEntityProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import InvoicesNavbar from '../common/InvoicesNavbar/InvoicesNavbar';
import InvoicingList from './InvoicingList';
import { handleAPIError } from 'common/errorHandler';
import { formatDateFilterInvoices } from '../../common/utils';
import ErrorSummary from 'components/common/ErrorSummary';
import { DateRange, ENTITY_TYPE } from 'common/constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { GridSortModel } from '@mui/x-data-grid';
import { Card } from '@mui/material';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

export type InvoicesInput = {
  to: string | null;
  number: string | null;
  date: string | null;
  from: string | null;
  range: DateRange | undefined;
  amount: number | null;
  amounts: object;
  idInvoiceType: number | null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type ValidatingInput = {
  idInvoiceType: number | null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: any;
};
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const Invoicing = () => {
  const [input, setInput] = useState<InvoicesInput | null>({
    to: '',
    number: '',
    date: '',
    from: '',
    range: undefined,
    amount: null,
    amounts: {},
    idInvoiceType: null,
    idWorkflowState: null,
    queryTerm: '',
    currentPage: 1,
    sortValue: '',
    orderValue: 'asc',
  });

  // Local state for search input
  const [queryString, setQueryString] = useState<string>('');

  const [paginationModel, setPaginationModel] = usePaginationPreference('invoicing', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'contactName', sort: 'asc' },
  ]);

  useEffect(() => {
    setInput((prev) => ({
      ...prev,
      currentPage: paginationModel.page + 1,
      sortValue: sortModel[0]?.field || '',
      orderValue: sortModel[0]?.sort || 'asc',
    }));
  }, [paginationModel, sortModel]);

  useEffect(() => {
    if (input.from && input.to) {
      setInput((prev) => ({
        ...prev,
        range: {
          from: new Date(input.from),
          to: new Date(input.to),
        },
      }));
    }
  }, [input.from, input.to]);

  const [invoicesOwner, setInvoicesOwner] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });

  const [validatingInvoices, setValidatingInvoices] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });

  const [invoiceStatus, setInvoiceStatus] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const [loadingValidating, setLoadingValidating] = useState<boolean>(false);

  const [errors, setErrors] = useState({});

  const api: Api = useApi();

  const toast = useToastMessageQueue();

  const entityType = getUserEntity().entityType;

  const [validatingLimit, setValidatingLimit] = useState<number>(() => {
    return parseInt(localStorage.getItem('itemsPerPageInvoicesValidating'), 10) || 10;
  });

  useEffect(() => {
    setValidatingLimit(parseInt(localStorage.getItem('itemsPerPageInvoicesValidating'), 10) || 10);
  }, []);

  const fetchInvoicesOwner = (
    term?: string,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    order?: string,
    f?: Array<string>
  ) => {
    setLoading(true);
    if (input.idWorkflowState) {
      f = [`idworkflowstate:${input.idWorkflowState}`];
    }
    if (input.range?.from) {
      let from = formatDateFilterInvoices(input.range.from);
      f = f || [];
      f.push(`startdate:${from}`);
    }
    if (input.range?.to) {
      let to = formatDateFilterInvoices(input.range.to);
      f = f || [];
      f.push(`enddate:${to}`);
    }
    if (Number(input.amounts['min'])) {
      f = f || [];
      f.push(`amountabove:${Number(input.amounts['min'])}`);
    }
    if (Number(input.amounts['max'])) {
      f = f || [];
      f.push(`amountbelow:${Number(input.amounts['max'])}`);
    }
    api.invoiceApi
      .apiVversionInvoiceSearchIssuedGet(
        '1',
        input.queryTerm,
        input.currentPage,
        paginationModel?.pageSize,
        input.sortValue,
        input.orderValue,
        f,
        {}
      )
      .then((response) => {
        if (response.data.data.queryResult) {
          setInvoicesOwner({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        } else {
          setInvoicesOwner({
            totalRecords: null,
            queryResult: null,
          });
        }
        console.log('Success');
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchInvoicesStatus = async () => {
    const response = await api.workflowApi
      .apiVversionModuleWorkflowStatesModuleIdGet(3, '1')
      .then((response) => {
        if (response.data.data.queryResult) {
          setInvoiceStatus({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  useEffect(() => {
    fetchInvoicesOwner();
    fetchInvoicesStatus();
  }, [input, paginationModel?.pageSize]);

  const onSearch = useCallback((queryStringValue, idStatus, range, amounts) => {
    setErrors([]);
    setQueryString(queryStringValue); // Keep the query string in local state
    setInput((prev) => ({
      ...prev,
      queryTerm: queryStringValue,
      idWorkflowState: idStatus,
      range: range,
      currentPage: 1,
      amounts: amounts,
    }));
  }, []);

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <div className="card mt-4">
          <div className="card-header">
            {
              <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                {intl.get('mainNavigation.invoice')}
              </Typography>
            }
          </div>
          <div className="card-body">
            <div>
              <InvoicesNavbar
                invoiceStatus={invoiceStatus?.queryResult}
                onSearch={onSearch}
                allInvoices={true}
                showPlusButton={entityType === ENTITY_TYPE.CORPORATE_ENTITY}
                queryString={queryString}
                setQueryString={setQueryString}
              />
              {loading === true ? (
                <Spinner />
              ) : (
                <>
                  <div className="col-md-12">
                    <ErrorSummary
                      errors={errors}
                      showEverything={false}
                      mode="warning"
                    ></ErrorSummary>
                  </div>
                  <InvoicingList
                    searchResult={invoicesOwner}
                    isValidating={false}
                    loading={loading}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </Box>
    </Card>
  );
};

export default Invoicing;
